import { useSelector } from 'react-redux';

import { Button, Dropdown, Input, Modal } from '@components';
import { DEFAULT_CURRENCY_CODE } from '@constants/common';
import { FEATURE_FLAGS } from '@constants/feature';
import {
  NAME_MAX_LENGTH,
  NAME_MIN_LENGTH,
  SettlementMode
} from '@constants/generic';
import { nameRegex } from '@constants/regex';
import { CurrencyConfig, DEFAULT_CURRENCY_CONFIG } from '@hooks/useCurrency';
import { BranchForm, ValidationError } from '@types';
import { DropdownOption } from 'types/dropdown';

interface CreateBranchModalProps {
  createBranchModalOpen: boolean;
  setCreateBranchModalOpen: (open: boolean) => void;
  setFormData: (data: BranchForm) => void;
  formData: BranchForm;
  onChangeBranchName: (value: string) => void;
  onChangeCurrency: (value: string) => void;
  error: ValidationError;
  onBlurBranchName: (value: string) => void;
  onClickSave: () => void;
}

const CreateBranchModal = ({
  createBranchModalOpen,
  setCreateBranchModalOpen,
  setFormData,
  formData,
  onChangeBranchName,
  onChangeCurrency,
  error,
  onBlurBranchName,
  onClickSave
}: CreateBranchModalProps) => {
  const handleCancel = () => {
    setCreateBranchModalOpen(false);
    setFormData({
      branchName: '',
      settlementMode: SettlementMode.BANK,
      currency: DEFAULT_CURRENCY_CODE
    });
  };

  const { appConfig } = useSelector((state: any) => state.user);
  const { orgFeatureFlags } = useSelector((state: any) => state.user);
  const isMultiCurrencySupported = orgFeatureFlags?.includes(
    FEATURE_FLAGS.MULTI_CURRENCY_SUPPORT
  );

  const currencyOptions: DropdownOption[] = (
    appConfig?.supported_currencies ?? [DEFAULT_CURRENCY_CONFIG]
  ).map((currency: CurrencyConfig) => ({
    id: currency.code,
    label: currency.code,
    value: currency.code,
    element: (
      <div className="flex items-center gap-0.5">
        {currency.code}
        <span className="font-inter">
          {currency.code !== currency.symbol ? ` (${currency.symbol})` : ''}
        </span>
      </div>
    )
  }));

  return (
    <Modal
      isOpen={createBranchModalOpen}
      onCancel={handleCancel}
      isCloseIconRequired
      closeIconClassName="!top-2"
      className="w-120 !bg-cofeeNeutral1"
    >
      <div className="border-b border-b-grey30">
        <div className="px-6 py-4 text-xl font-semibold text-cofeeNeutral10">
          New Branch
        </div>
      </div>
      <div className="px-6 py-4">
        <div className="mb-0.5 text-sm font-semibold text-cofeeNeutral10">
          Branch Name
        </div>
        <Input
          value={formData.branchName}
          inputStyle="!bg-cofeeNeutral1"
          handleInputChange={onChangeBranchName}
          placeholder="Enter branch name"
          inputRegex={nameRegex}
          minLength={NAME_MIN_LENGTH}
          maxLength={NAME_MAX_LENGTH}
          error={!!error.name}
          errorMessage={error.name}
          handleInputBlur={onBlurBranchName}
        />
        {isMultiCurrencySupported && currencyOptions.length > 0 && (
          <div className="mt-4">
            <div className="mb-0.5 text-sm font-semibold text-cofeeNeutral10">
              Currency
            </div>
            <Dropdown
              filterOptions={currencyOptions}
              currentFilter={formData.currency ?? DEFAULT_CURRENCY_CODE}
              onSelectFilter={onChangeCurrency}
              usePortal
              placeholder="Select currency"
              placeholderStyle="!text-cofeeNeutral7"
              className="!w-full"
              searchBarPlaceholder="Search currency"
            />
          </div>
        )}
      </div>
      <div className="flex w-full items-center justify-end gap-4 border-t border-cofeeNeutral4 px-6 py-4">
        <Button
          label="Cancel"
          size="medium"
          variant="tertiary"
          handleClick={handleCancel}
          labelStyle="!text-cofeeNeutral10 !font-medium"
        />
        <Button
          label="Create"
          size="medium"
          variant="primary"
          handleClick={onClickSave}
          disabled={formData.branchName.length < NAME_MIN_LENGTH}
        />
      </div>
    </Modal>
  );
};

export default CreateBranchModal;
